// Iter 334 (Feb 2026), P2: Vendor-Credentials Admin-Karte
// ────────────────────────────────────────────────────────────────────────
// Zentrale Verwaltung von Bestell-Links + Login-Daten für Lieferanten.
// Zugriff: Admin + Trainer.
// Passwörter werden verschlüsselt gespeichert und nur via expliziten
// "Anzeigen"-Klick im Klartext geladen (zusätzlicher Backend-Roundtrip).
//
// Backend: /api/vendor-credentials (CRUD + /:id/password)
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Lock,
  Plus,
  Eye,
  EyeOff,
  Copy,
  Edit,
  Trash2,
  ExternalLink,
  Search,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { VendorBackupCard } from '@/components/VendorBackupCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: '',
  link: '',
  username: '',
  password: '',
  notes: '',
};

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const VendorCredentialsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [revealed, setRevealed] = useState({}); // {id: plaintextPassword}
  const [search, setSearch] = useState('');

  const fetchCredentials = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/vendor-credentials`, {
        headers: authHeader(),
      });
      setCredentials(res.data.credentials || []);
    } catch (e) {
      console.error('VendorCredentials Error:', e);
      toast.error('Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCredentials();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (cred) => {
    setEditingId(cred.id);
    // Passwort wird NICHT vorgefüllt — User muss es bewusst neu eingeben.
    // (Leer lassen = Passwort bleibt unverändert).
    setForm({
      name: cred.name || '',
      link: cred.link || '',
      username: cred.username || '',
      password: '',
      notes: cred.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Bitte einen Namen eingeben');
      return;
    }
    try {
      if (editingId) {
        // PUT — Passwort nur senden wenn der User es geändert hat
        const payload = {
          name: form.name,
          link: form.link,
          username: form.username,
          notes: form.notes,
        };
        if (form.password) payload.password = form.password;
        await axios.put(`${API}/vendor-credentials/${editingId}`, payload, {
          headers: authHeader(),
        });
        toast.success('Eintrag aktualisiert');
      } else {
        await axios.post(`${API}/vendor-credentials`, form, {
          headers: authHeader(),
        });
        toast.success('Eintrag erstellt');
      }
      setDialogOpen(false);
      resetForm();
      fetchCredentials();
    } catch (e) {
      console.error('Save error:', e);
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };

  const handleDelete = async (cred) => {
    if (!window.confirm(`"${cred.name}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/vendor-credentials/${cred.id}`, {
        headers: authHeader(),
      });
      toast.success('Eintrag gelöscht');
      // Falls revealed → cleanup
      setRevealed((prev) => {
        const copy = { ...prev };
        delete copy[cred.id];
        return copy;
      });
      fetchCredentials();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const togglePassword = async (cred) => {
    if (revealed[cred.id]) {
      // Hide
      setRevealed((prev) => {
        const copy = { ...prev };
        delete copy[cred.id];
        return copy;
      });
      return;
    }
    try {
      const res = await axios.get(
        `${API}/vendor-credentials/${cred.id}/password`,
        { headers: authHeader() }
      );
      setRevealed((prev) => ({ ...prev, [cred.id]: res.data.password }));
    } catch (e) {
      toast.error('Passwort konnte nicht geladen werden');
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopiert`);
    } catch {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  const copyPassword = async (cred) => {
    try {
      const res = await axios.get(
        `${API}/vendor-credentials/${cred.id}/password`,
        { headers: authHeader() }
      );
      await navigator.clipboard.writeText(res.data.password);
      toast.success('Passwort kopiert');
    } catch {
      toast.error('Passwort konnte nicht kopiert werden');
    }
  };

  // Suche / Filter (4b: nach Name + Notizen + Username)
  const filtered = search
    ? credentials.filter((c) => {
        const q = search.toLowerCase();
        return (
          (c.name || '').toLowerCase().includes(q) ||
          (c.username || '').toLowerCase().includes(q) ||
          (c.notes || '').toLowerCase().includes(q)
        );
      })
    : credentials;

  return (
    <div className="space-y-6 p-6" data-testid="vendor-credentials-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Lock size={28} />
            Bestell-Logins
          </h1>
          <p className="text-gray-600 mt-2 max-w-2xl">
            Zentrale Sammlung von Bestell-Links und Login-Daten für Lieferanten,
            Shops und Portale. Passwörter werden verschlüsselt gespeichert und
            nur auf expliziten Klick angezeigt.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-teal-600 hover:bg-teal-700"
          data-testid="vendor-add-btn"
        >
          <Plus size={16} className="mr-2" />
          Neuer Eintrag
        </Button>
      </div>

      {/* Backup-Status-Karte (Iter 335, P1) — Warnung wenn Key nicht gesichert */}
      <VendorBackupCard isAdmin={isAdmin} />

      {/* Search */}
      <div className="relative max-w-md">
        <Search
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <Input
          type="text"
          placeholder="Suche nach Name, Username oder Notiz…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="vendor-search"
        />
      </div>

      {/* Liste */}
      {loading ? (
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-600 border-t-transparent" />
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            {search
              ? 'Keine Treffer für deine Suche.'
              : 'Noch keine Einträge — klicke oben rechts auf „Neuer Eintrag".'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((cred) => {
            const isRevealed = !!revealed[cred.id];
            return (
              <Card
                key={cred.id}
                className="hover:shadow-md transition-shadow"
                data-testid={`vendor-card-${cred.id}`}
              >
                <CardContent className="p-5 space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-lg leading-tight break-words">
                      {cred.name}
                    </h3>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(cred)}
                        data-testid={`vendor-edit-${cred.id}`}
                        title="Bearbeiten"
                      >
                        <Edit size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(cred)}
                        data-testid={`vendor-delete-${cred.id}`}
                        title="Löschen"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>

                  {/* Link */}
                  {cred.link && (
                    <div className="text-sm flex items-center gap-2">
                      <a
                        href={cred.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-600 hover:underline truncate flex items-center gap-1"
                        data-testid={`vendor-link-${cred.id}`}
                      >
                        <ExternalLink size={12} />
                        {cred.link.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}

                  {/* Username */}
                  {cred.username && (
                    <div className="text-sm">
                      <div className="text-xs text-gray-500 mb-0.5">Benutzer</div>
                      <div className="flex items-center justify-between gap-2 bg-gray-50 rounded px-2 py-1 font-mono">
                        <span className="truncate">{cred.username}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2"
                          onClick={() => copyToClipboard(cred.username, 'Benutzer')}
                          title="Kopieren"
                        >
                          <Copy size={12} />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Password */}
                  {cred.has_password && (
                    <div className="text-sm">
                      <div className="text-xs text-gray-500 mb-0.5">Passwort</div>
                      <div className="flex items-center justify-between gap-2 bg-gray-50 rounded px-2 py-1 font-mono">
                        <span className="truncate">
                          {isRevealed ? revealed[cred.id] : '••••••••••'}
                        </span>
                        <div className="flex gap-0.5 shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2"
                            onClick={() => togglePassword(cred)}
                            data-testid={`vendor-toggle-pw-${cred.id}`}
                            title={isRevealed ? 'Verbergen' : 'Anzeigen'}
                          >
                            {isRevealed ? <EyeOff size={12} /> : <Eye size={12} />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2"
                            onClick={() => copyPassword(cred)}
                            title="Passwort kopieren"
                          >
                            <Copy size={12} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {cred.notes && (
                    <div className="text-sm text-gray-700 border-t pt-2 mt-2 whitespace-pre-wrap break-words">
                      {cred.notes}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="text-[10px] text-gray-400 border-t pt-2 mt-2">
                    Zuletzt bearbeitet:{' '}
                    {cred.updated_at
                      ? new Date(cred.updated_at).toLocaleString('de-CH', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })
                      : '—'}
                    {cred.updated_by ? ` · ${cred.updated_by}` : ''}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="vendor-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="vendor-name">Name *</Label>
              <Input
                id="vendor-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="z.B. Amazon Business"
                data-testid="vendor-input-name"
              />
            </div>
            <div>
              <Label htmlFor="vendor-link">Link / URL</Label>
              <Input
                id="vendor-link"
                type="url"
                value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })}
                placeholder="https://…"
                data-testid="vendor-input-link"
              />
            </div>
            <div>
              <Label htmlFor="vendor-username">Benutzer</Label>
              <Input
                id="vendor-username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="einkauf@strongfit.ch"
                autoComplete="off"
                data-testid="vendor-input-username"
              />
            </div>
            <div>
              <Label htmlFor="vendor-password">
                Passwort {editingId && '(leer = unverändert)'}
              </Label>
              <Input
                id="vendor-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editingId ? '••••••••••' : 'Passwort'}
                autoComplete="new-password"
                data-testid="vendor-input-password"
              />
            </div>
            <div>
              <Label htmlFor="vendor-notes">Notizen</Label>
              <Textarea
                id="vendor-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="z.B. Kundennr. 12345, bevorzugt per Sammelrechnung…"
                rows={3}
                data-testid="vendor-input-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDialogOpen(false);
                resetForm();
              }}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleSave}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="vendor-save-btn"
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorCredentialsPage;
